import { useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSession } from '../lib/session.js';
import { api, RequestError } from '../lib/api.js';
import { Button, ErrorNotice, Field, Input } from '../components/ui.js';
import { AuthShell } from '../components/AuthShell.js';
import { GoogleButton } from '../components/GoogleButton.js';
import { SignupPending } from './SignupPage.js';

/**
 * The reasons the Google round-trip can come back empty-handed — including an
 * account Worki has not opened yet or has closed (1.34), which the server turns
 * away with `accountPending` and `accountSuspended`. An allow-list and not
 * `errors.${param}`: the reason arrives in the address bar, where anyone can
 * write anything, and an unknown key would be rendered raw.
 */
const GOOGLE_ERRORS = new Set([
  'googleFailed',
  'googleCancelled',
  'googleDisabled',
  'accountDisabled',
  'accountPending',
  'accountSuspended',
]);

/**
 * Log-in is a code sent to the address, or Google — not a password: the address
 * is the account, and there is nothing stored here that a leak could reuse.
 */
export function LoginPage() {
  const { t } = useTranslation();
  const { login } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();

  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const from = (location.state as { from?: string } | null)?.from;
  const returned = params.get('error');
  const googleError = returned && GOOGLE_ERRORS.has(returned) ? `errors.${returned}` : '';

  const sendCode = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      await api.post('/auth/request-login-code', { email });
      setStep('code');
    } catch (err) {
      setError(err instanceof RequestError ? err.code : 'errors.unknown');
    } finally {
      setBusy(false);
    }
  };

  const verify = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      await login({ email, code });
      // Back to whatever was being opened when the session ran out.
      navigate(from ?? '/', { replace: true });
    } catch (err) {
      setError(err instanceof RequestError ? err.code : 'errors.unknown');
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell
      title={t('auth.loginTitle')}
      subtitle={step === 'email' ? t('auth.loginSubtitle') : t('auth.codeSent', { email })}
      onSubmit={step === 'email' ? sendCode : verify}
      footer={
        <>
          {t('auth.noAccount')}{' '}
          <Link to="/signup" className="text-primary font-semibold hover:underline">
            {t('auth.signup')}
          </Link>
        </>
      }
    >
      {(error || googleError) === 'errors.accountPending'
        ? <SignupPending />
        : (error || googleError) && <ErrorNotice code={error || googleError} />}

      {/* Only on the first step: once a code is on its way to an address,
          offering a different way in beside it is a way to lose the code. */}
      {step === 'email' && <GoogleButton {...(from ? { returnTo: from } : {})} />}

      <Field label={t('auth.email')} htmlFor="email">
        <Input
          id="email"
          type="email"
          autoComplete="email"
          dir="ltr"
          value={email}
          disabled={step === 'code'}
          onChange={(event) => setEmail(event.target.value)}
        />
      </Field>

      {step === 'code' && (
        <Field label={t('auth.otp')} htmlFor="code" hint={t('auth.codeExpiry')}>
          <Input
            id="code"
            autoFocus
            inputMode="numeric"
            autoComplete="one-time-code"
            dir="ltr"
            maxLength={6}
            value={code}
            onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
          />
        </Field>
      )}

      <Button
        type="submit"
        variant="primary"
        size="lg"
        block
        loading={busy}
        disabled={step === 'email' ? !email : code.length !== 6}
      >
        {step === 'email' ? t('auth.sendCode') : t('auth.login')}
      </Button>

      {step === 'code' && (
        <Button
          type="button"
          variant="ghost"
          block
          onClick={() => {
            setStep('email');
            setCode('');
          }}
        >
          {t('auth.changeEmail')}
        </Button>
      )}
    </AuthShell>
  );
}
